import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { parseWarehouseError, warehouseApi } from '../api/warehouse.api';
import { downloadCsv, norm, parseCsvLines, readCsvFile } from '../format';
import { rowErrorsOf } from '../hooks/useServerErrors';
import type {
  CsvImportError,
  FieldError,
  ImportVendorsResult,
  VendorRowPayload,
} from '../types';
import { CsvErrorTable, CsvFileProblem } from './CsvErrorTable';
import {
  describeUploadError,
  localFileProblem,
  type PickError,
} from './csvImportErrors';
import { DisabledReason } from './dialogs';
import { codeCls, metaCls, nameCls } from './ui';
import { VENDOR_CODE } from './VendorFormModal';

/** Rows one import may carry (the server's limit). */
export const VENDOR_IMPORT_MAX = 1000;

/** Template — the columns of ops' vendor sheet ("No" is just the row number). */
export const VENDOR_CSV_HEADERS = [
  'No',
  'ID Vendor',
  'Vendor Name',
  'Vendor Address',
  'Vendor Phone',
];

type VendorKey = 'code' | 'name' | 'address' | 'phone';

const LABEL: Record<VendorKey, string> = {
  code: 'ID Vendor',
  name: 'Vendor Name',
  address: 'Vendor Address',
  phone: 'Vendor Phone',
};

/** Sheet headers; the client-sheet names ops started from are accepted too. */
const HEADER_KEY = new Map<string, VendorKey>([
  ['idvendor', 'code'],
  ['vendorname', 'name'],
  ['vendoraddress', 'address'],
  ['vendorphone', 'phone'],
  ['idclient', 'code'],
  ['clientname', 'name'],
  ['clientaddress', 'address'],
  ['clientphone', 'phone'],
  ['code', 'code'],
  ['name', 'name'],
  ['address', 'address'],
  ['phone', 'phone'],
]);

export interface ParsedVendorRow {
  line: number;
  code: string;
  name: string;
  address: string;
  phone: string;
  value: VendorRowPayload | null;
  errors: FieldError[];
  /** Repeats an earlier row's code / name — the server will skip it. */
  repeat: boolean;
}

/**
 * Read ops' vendor sheet as-is: blank padding rows / columns skipped, header
 * = the first row with a "Vendor Name" cell, headers matched ignoring case and
 * spaces, "No" ignored.
 */
export const parseVendorCsv = (text: string): ParsedVendorRow[] | null => {
  const lines = parseCsvLines(text);
  const headerAt = lines.findIndex((l) =>
    l.cells.some((c) => {
      const k = HEADER_KEY.get(norm(c));
      return k === 'name';
    })
  );
  if (headerAt < 0) return null;
  const colOf = new Map<VendorKey, number>();
  lines[headerAt].cells.forEach((h, i) => {
    const k = HEADER_KEY.get(norm(h));
    if (k && !colOf.has(k)) colOf.set(k, i);
  });
  const seen = new Set<string>();
  return lines.slice(headerAt + 1).map(({ line, cells }) => {
    const cell = (k: VendorKey) => {
      const i = colOf.get(k);
      return i === undefined ? '' : (cells[i] ?? '').trim();
    };
    const code = cell('code').toUpperCase();
    const name = cell('name');
    const address = cell('address');
    const phone = cell('phone');
    const errors: FieldError[] = [];
    if (!name)
      errors.push({ field: 'name', message: 'Vendor Name wajib diisi.' });
    if (code && !VENDOR_CODE.test(code))
      errors.push({
        field: 'code',
        message: 'ID Vendor minimal 2 karakter: huruf, angka, . _ / -',
      });
    const keys = [code && `c:${code}`, name && `n:${name.toLowerCase()}`];
    const repeat = keys.some((k) => k && seen.has(k));
    keys.forEach((k) => k && seen.add(k));
    return {
      line,
      code,
      name,
      address,
      phone,
      errors,
      repeat,
      value: errors.length
        ? null
        : {
            name,
            ...(code ? { code } : {}),
            ...(address ? { address } : {}),
            ...(phone ? { phone } : {}),
          },
    };
  });
};

/**
 * Every field error of the parsed rows (local and server), one per problem,
 * for the shared error table: the CSV row, the sheet column (its header), the
 * cell as the file has it, and why.
 */
export const vendorRowErrors = (
  rows: readonly ParsedVendorRow[]
): CsvImportError[] =>
  rows.flatMap((r) =>
    r.errors.map((e) => ({
      row: r.line,
      field: LABEL[e.field as VendorKey] ?? e.field,
      value: e.field in LABEL ? r[e.field as VendorKey] : '',
      message: e.message,
    }))
  );

const errorText = (errors: FieldError[]) =>
  errors
    .map((e) => {
      const label = LABEL[e.field as VendorKey];
      return label && !e.message.startsWith(label)
        ? `${label}: ${e.message}`
        : e.message;
    })
    .join(' · ');

const COLUMNS: TableDataColumn<ParsedVendorRow>[] = [
  {
    key: 'line',
    title: 'Baris',
    render: (_v, r) => <span className="tabular-nums">{r.line}</span>,
  },
  {
    key: 'code',
    title: 'ID Vendor',
    render: (_v, r) =>
      r.code ? (
        <span className={codeCls}>{r.code}</span>
      ) : (
        <span className={metaCls}>otomatis</span>
      ),
  },
  {
    key: 'name',
    title: 'Vendor Name',
    render: (_v, r) => <span className={nameCls}>{r.name || '—'}</span>,
  },
  {
    key: 'address',
    title: 'Vendor Address',
    render: (_v, r) => (
      <span className={`${metaCls} line-clamp-2 max-w-[220px]`}>
        {r.address || '—'}
      </span>
    ),
  },
  { key: 'phone', title: 'Vendor Phone' },
  {
    key: 'check',
    title: 'Cek',
    render: (_v, r) =>
      r.errors.length ? (
        <span
          className="text-[12px] text-(--state-error-dark)"
          data-testid={`vendor-row-error-${r.line}`}
        >
          {errorText(r.errors)}
        </span>
      ) : r.repeat ? (
        <span className={metaCls}>Duplikat di file — dilewati</span>
      ) : (
        <Badge status="success" size="sm" type="dot">
          OK
        </Badge>
      ),
  },
];

/** Import vendors from ops' sheet (CSV); existing code / name are skipped. */
export const VendorImportModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onImported: (res: ImportVendorsResult) => void;
}> = ({ open, onClose, onImported }) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedVendorRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PickError | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setFileName(null);
    setRows([]);
    setError(null);
    setBusy(false);
  }, [open]);

  const invalid = useMemo(
    () => rows.filter((r) => r.errors.length).length,
    [rows]
  );

  const rowErrors = useMemo(() => vendorRowErrors(rows), [rows]);

  if (!open) return null;

  const readFile = async (f: File | null) => {
    setError(null);
    if (!f) return;
    const read = await readCsvFile(f);
    if (read.error !== null) {
      setError(
        localFileProblem(
          read.error,
          'Simpan sheet sebagai .csv, atau unduh template lalu salin datanya ke sana.'
        )
      );
      return;
    }
    const parsed = parseVendorCsv(read.text);
    if (!parsed) {
      setError(
        localFileProblem(
          'Header "Vendor Name" tidak ditemukan — unduh template untuk format yang benar.',
          'Baris header harus punya kolom "Vendor Name". Salin data ke template (nama kolom di baris pertama), lalu pilih ulang file.'
        )
      );
      return;
    }
    if (!parsed.length) {
      setError(
        localFileProblem(
          'File tidak punya baris data.',
          'Isi minimal satu baris vendor di bawah header, lalu pilih ulang file.'
        )
      );
      return;
    }
    if (parsed.length > VENDOR_IMPORT_MAX) {
      setError(
        localFileProblem(
          `Maksimal ${VENDOR_IMPORT_MAX} vendor per import — file ini ${parsed.length} baris.`,
          'Pecah jadi beberapa file, lalu import satu per satu.'
        )
      );
      return;
    }
    setFileName(f.name);
    setRows(parsed);
  };

  const submit = async () => {
    const payload = rows.flatMap((r) => (r.value ? [r.value] : []));
    if (!rows.length || invalid || payload.length !== rows.length) return;
    setBusy(true);
    setError(null);
    try {
      onImported(await warehouseApi.importVendors(payload));
    } catch (err) {
      // A 422 names its rows (rows[i].<field>): show them on the preview.
      const byRow = rowErrorsOf(err);
      if (byRow.size) {
        setRows((prev) =>
          prev.map((r, i) =>
            byRow.has(i) ? { ...r, value: null, errors: byRow.get(i) ?? [] } : r
          )
        );
      } else
        setError(
          describeUploadError(parseWarehouseError(err), {
            fileHint:
              'Unduh template terbaru, salin data ke sana, lalu pilih ulang file.',
            requestTitle: 'Import gagal',
            requestAction: 'Import',
          })
        );
    } finally {
      setBusy(false);
    }
  };

  /** Why Import is off (visible next to it). */
  const blocked = !rows.length
    ? 'Pilih file CSV'
    : invalid > 0
      ? `${invalid} baris error — perbaiki dulu`
      : null;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon="ri-upload-2-line"
      title="Import vendor (CSV)"
      width="xl"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <div className="flex items-center justify-end gap-2">
          {blocked && (
            <DisabledReason testId="vendor-import-reason" className="mr-auto">
              {blocked}
            </DisabledReason>
          )}
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={busy}
          >
            Batal
          </Button>
          <Button
            onClick={() => void submit()}
            loading={busy}
            disabled={!!blocked}
            data-testid="vendor-import-submit"
          >
            {rows.length && !invalid
              ? `Import ${rows.length} vendor`
              : 'Import'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="vendor-import-file"
            onChange={(e) => {
              void readFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <Button
            tone="neutral"
            style="stroke"
            onClick={() => inputRef.current?.click()}
            leftIcon={<i className="ri-file-upload-line text-[14px]" />}
          >
            {fileName ? 'Ganti file' : 'Pilih file CSV'}
          </Button>
          {fileName && <span className={metaCls}>{fileName}</span>}
          <span className="flex-1" />
          <Button
            tone="primary"
            style="link"
            size="xs"
            className="text-xs font-semibold"
            onClick={() =>
              downloadCsv('template-vendor.csv', [VENDOR_CSV_HEADERS])
            }
            leftIcon={
              <i className="ri-download-2-line text-[14px]" aria-hidden />
            }
          >
            Download template
          </Button>
        </div>
        {error && (
          <CsvFileProblem problem={error} testId="vendor-import-error" />
        )}
        {invalid > 0 && (
          <Alert
            status="warning"
            appearance="stroke"
            size="sm"
            title={`${invalid} baris belum valid — perbaiki file lalu pilih ulang.`}
            data-testid="vendor-import-invalid"
          />
        )}
        {rowErrors.length > 0 && (
          <CsvErrorTable
            errors={rowErrors}
            fileName={fileName}
            maxHeight={260}
          />
        )}
        {rows.length > 0 && (
          <div className="max-h-[360px] overflow-auto">
            <TableData size="sm" data={rows} columns={COLUMNS} />
          </div>
        )}
      </div>
    </Modal>
  );
};
